import type { OrderDetails } from '@/entities/order/model'
import { notifications } from '@mantine/notifications'
import { getOrderType, useAcknowledgeOrder } from '@/entities/order/lib'
import { useOrderStatusMutation, useRawOrdersQuery } from '@/entities/order/model'
import { useCollectionCodeEnabled, useDisplayStateByStatus, useStateConfigQuery, useTmInitialsEnabled } from '@/entities/state/model'
import { useCollectionCodeModal } from '@/features/order/ui/collection-code-modal'
import { useTmInitialsStore } from '@/features/order/ui/initials-capture-modal'
import { useNoBarcodeCompletionConfirm } from '@/features/order/ui/no-barcode-confirmation'
import { CRATE_SCAN_PENDING_MESSAGE, useCrateScanGate } from './useCrateScanGate'
import { moveOrdersTogether } from './useGroupQuickActions'
import { useGroupTransitionGuard } from './useGroupTransitionGuard'
import { useOrderGroupMembers } from './useOrderGroupMembers'
import { notifyOrderScanIncomplete, useOrderTransitionGuard } from './useOrderTransitionGuard'
import { usePoaGate } from './usePoaGate'

/**
 * Hook to get quick actions for an order.
 * These are presented as buttons on the order card.
 *
 * @param order - The order to get quick actions for
 * @returns An object containing the primary action and handlers for it
 */
export function useQuickActions(order: OrderDetails | undefined) {
  const { data: quickButtonStates } = useDisplayStateByStatus()
  const { data: stateConfig } = useStateConfigQuery()
  const isCollectionCodeEnabled = useCollectionCodeEnabled()
  const isTmInitialsEnabled = useTmInitialsEnabled()
  const { mutateAsync: updateStatus } = useOrderStatusMutation()
  const { updateOrderAcknowledgement } = useAcknowledgeOrder()
  const { open: openCollectionCodeModal } = useCollectionCodeModal()
  const enqueueInitials = useTmInitialsStore(state => state.enqueue)
  const { shouldGateCompletion, openPoaChecks } = usePoaGate()
  const { isCrateScanPending } = useCrateScanGate()
  const { requiresConfirm, requestConfirm } = useNoBarcodeCompletionConfirm()

  // BD-8186: a member of a group must not advance while the group still has crates outstanding.
  const { isBlockedByGroupScan, isTransitionBlocked } = useGroupTransitionGuard(order?.orderId)
  // BD-8644: the same rule for a single order, which otherwise relies on the backend to reject.
  const { isOrderTransitionBlocked } = useOrderTransitionGuard(order)

  // BD-8130: a grouped order moves with its partners, never on its own.
  const { data: allOrders } = useRawOrdersQuery()
  const groupMemberOrderIds = useOrderGroupMembers(order?.orderId ?? '')
  const groupMemberOrders = groupMemberOrderIds.length > 1
    ? (allOrders ?? []).filter(member => groupMemberOrderIds.includes(member.orderId))
    : []
  const isGrouped = groupMemberOrders.length > 1
  const actionOrders = isGrouped ? groupMemberOrders : (order ? [order] : [])

  // After a successful completion, queue the completed order for team member
  // initials capture. Single orders only: a group asks once for all of its
  // orders in moveOrdersTogether (decoupled: completion and its confetti are
  // unaffected).
  const queueInitialsIfCompleted = (action: string) => {
    if (action === 'Completed' && isTmInitialsEnabled) {
      enqueueInitials({ orderId: order.orderId, customerName: order.customerName })
    }
  }

  const performAction = async (action: string) => {
    if (!order) {
      return
    }

    if (isGrouped) {
      await moveOrdersTogether({
        orders: groupMemberOrders,
        action,
        updateStatus,
        updateOrderAcknowledgement,
        enqueueInitials: isTmInitialsEnabled ? enqueueInitials : undefined,
      })
      return
    }

    // For Arrived orders, acknowledge first before updating status
    if (order.status === 'Arrived' && !order.isAcknowledged) {
      try {
        // Acknowledge the order first
        await updateOrderAcknowledgement(order)
        // Then update the status after acknowledgement succeeds
        await updateStatus({
          orderId: order.orderId,
          newStatus: action,
        })
        queueInitialsIfCompleted(action)
      } catch (error) {
        console.error('Failed to acknowledge or update order:', error)
      }
    } else {
      // For non-Arrived orders or already acknowledged orders, just update status
      await updateStatus({
        orderId: order.orderId,
        newStatus: action,
      })
      queueInitialsIfCompleted(action)
    }
  }

  const handleAction = async (action: string) => {
    if (!order) {
      return
    }

    // Silent when BD-8185 shows "Go to next grouped order" instead of a blocked "We're on it".
    if (isTransitionBlocked(action)) {
      return
    }
    if (isOrderTransitionBlocked(action)) {
      notifyOrderScanIncomplete()
      return
    }

    // BD-8589: completing without array_barcodes waits for the team member to confirm.
    if (requiresConfirm(actionOrders, action)) {
      requestConfirm(() => void performAction(action))
      return
    }

    await performAction(action)
  }

  const statusDisplayState = order ? quickButtonStates?.[order.status] : undefined
  const primaryAction = statusDisplayState?.quickButtons?.[0]
  const statusColor = statusDisplayState?.color
  const normalizedOrderType = order ? getOrderType(order.hsFields)?.trim().toLowerCase() : undefined
  const configuredEligibleOrderTypes = (stateConfig?.collectCodeEligibleOrderTypes ?? [])
    .map(type => type.trim().toLowerCase())
    .filter(Boolean)
  const isCollectionCodeEligibleOrder = configuredEligibleOrderTypes.includes(normalizedOrderType ?? '')

  const handleCompletionAction = async (action = 'Completed') => {
    if (!order) {
      return
    }

    // Checked before the collection-code branch so a blocked transition never opens its modal.
    if (isTransitionBlocked(action)) {
      return
    }
    if (isOrderTransitionBlocked(action)) {
      notifyOrderScanIncomplete()
      return
    }

    const proceed = async () => {
      // BD-8245: a restricted order opens the Proof of Age checks instead of
      // completing. Checked before the collection code so the legally required
      // checks always run first.
      if (action === 'Completed' && shouldGateCompletion(order)) {
        // Crate scanning is resolved before the completion moment, so an order with
        // crates still unaccounted for must not reach the checks. Without this the
        // team member completes the whole flow and only then does the backend reject
        // the completion for the unscanned crates.
        if (isCrateScanPending(order)) {
          notifications.show({
            message: CRATE_SCAN_PENDING_MESSAGE,
            color: 'red',
          })
          return
        }
        openPoaChecks(order.orderId)
        return
      }

      // Collection code flow is only for "Complete" action.
      if (action === 'Completed' && isCollectionCodeEnabled && isCollectionCodeEligibleOrder) {
        openCollectionCodeModal({
          orderId: order.orderId,
          nextStatus: action,
          lastUpdateTime: order.lastUpdateTime,
        })
        return
      }

      await performAction(action)
    }

    // BD-8589: asked before the collection-code step, so backing out triggers nothing else.
    if (requiresConfirm(actionOrders, action)) {
      requestConfirm(() => void proceed())
      return
    }

    await proceed()
  }

  const handlePrimaryAction = async () => {
    if (primaryAction) {
      await handleCompletionAction(primaryAction.action)
    }
  }

  return {
    primaryAction,
    handlePrimaryAction,
    handleCompletionAction,
    handleAction,
    statusColor,
    isBlockedByGroupScan,
  }
}
